import {
  ArrowSquareOutIcon,
  CalendarBlankIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  DotsThreeIcon,
  Icon,
  MapPinIcon,
  PhoneSlashIcon,
  ProhibitIcon,
  ShieldIcon,
  ShieldWarningIcon,
} from '@phosphor-icons/react'
import { useState } from 'react'
import { RichTextEditor } from 'app/components/editor/RichTextEditor'
import { closeModal, showModal } from 'app/lib/modal'
import { Button } from 'app/components/ui/button'
import { Checkbox } from 'app/components/ui/checkbox'
import { cn } from 'app/lib/utils'
import { ContactJson, LOST_REASONS } from 'app/types/entities'
import { isSMCOrg } from 'lib/isSMCOrg'

export type ClosedLostSubmit = {
  closedLostReason: string
  disqualified?: string
  note?: string
  owner?: { id: string; name: string }
}

/**
 * The reasons, in the order somebody thinks of them rather than the order the
 * enum happens to hold them.
 *
 * Money first, because it is the most common answer and the one a practice can
 * act on. Then the two that are about reaching them at all, then the two about
 * whether this practice was ever the right one, then the one where somebody
 * else got there first.
 *
 * `Closed Lost: Other` is skipped on purpose: it is an internal tag variant of
 * `Other`, and offering both would ask staff to choose between two words for
 * one thing.
 */
const REASONS: { value: string; icon: Icon; hint: string; wide?: boolean }[] = [
  { value: `Insurance: Out of Network`, icon: ShieldWarningIcon, hint: `We do not take their plan` },
  { value: `Insurance: State / HMO`, icon: ShieldIcon, hint: `State or HMO coverage` },
  { value: `Pricing Too High`, icon: CurrencyDollarIcon, hint: `The quote was the problem` },
  { value: `Schedule Availability`, icon: CalendarBlankIcon, hint: `Nothing worked for them` },
  { value: `Unable to Contact`, icon: PhoneSlashIcon, hint: `Never reached them` },
  { value: `Service Not Offered`, icon: ProhibitIcon, hint: `We do not do that here` },
  { value: `Office Too Far`, icon: MapPinIcon, hint: `Too far to travel` },
  { value: `Scheduled with Competitor`, icon: ArrowSquareOutIcon, hint: `They went elsewhere` },
  // Across the bottom rather than alongside: it is the catch-all, not the
  // ninth option, and left in the grid it sat alone in one column with a hole
  // beside it.
  { value: `Other`, icon: DotsThreeIcon, hint: `None of the above`, wide: true },
].filter((reason) => LOST_REASONS.includes(reason.value as (typeof LOST_REASONS)[number]))

type QuickFormProps = {
  contact: ContactJson
  user: { id?: string; name?: string; orgId?: string }
  onSubmit: (data: ClosedLostSubmit) => void
  onCancel: () => void
}

/**
 * Opens the quick closed-lost form through the app's modal system. Every
 * "why do we lose leads" number downstream is built from the answer, and the
 * note is the same markdown editor the drawer's full form uses.
 */
export function showClosedLostQuickModal(props: QuickFormProps) {
  // `closeModal` synchronously calls this modal's `onClose`, which is `done`
  // itself, so `done` must settle once or the two recurse.
  let settled = false
  const done = (fn: () => void) => {
    if (settled) return
    settled = true
    closeModal()
    fn()
  }
  showModal(
    <ClosedLostQuickForm
      {...props}
      onSubmit={(data) => done(() => props.onSubmit(data))}
      onCancel={() => done(props.onCancel)}
    />,
    {
      useRadix: true,
      isFullScreen: false,
      // The form carries its own header (glyph, title, subtitle), so the
      // modal's title bar stays off and the panel narrows to the form.
      hideTitle: true,
      removePadding: true,
      overflowHidden: true,
      panelClassName: `min-w-0 sm:max-w-xl`,
      onClose: () => done(props.onCancel),
    },
  )
}

export function ClosedLostQuickForm({ contact, user, onSubmit, onCancel }: QuickFormProps) {
  const [reason, setReason] = useState<string | undefined>(contact.closedLostReason ?? undefined)
  const [note, setNote] = useState(``)
  const [disqualify, setDisqualify] = useState(false)
  const isSMC = isSMCOrg(user.orgId ?? ``)

  const submit = () => {
    if (!reason) return
    onSubmit({
      closedLostReason: reason,
      disqualified: disqualify ? `true` : `false`,
      note: note.trim() || undefined,
      owner: user.id && user.name ? { id: user.id, name: user.name } : undefined,
    })
  }

  return (
    <div className="flex flex-col text-left">
      <div className="flex flex-row items-start gap-3 border-b border-default px-6 pb-4 pt-5">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-600">
          <ProhibitIcon size={17} weight="bold" />
        </span>
        <div className="min-w-0">
          <h2 className="text-ui-md text-strong">Close Lead: {contact.name || `this contact`}</h2>
          <p className="mt-0.5 text-body-sm text-subtle">
            They come off the board. The reason goes on their record, and it is where every "why do we lose leads"
            number comes from.
          </p>
        </div>
      </div>

      <div className="max-h-[60vh] space-y-5 overflow-y-auto px-6 py-5">
        <div>
          <p className="text-label-sm text-default">What happened?</p>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {REASONS.map((option) => {
              const selected = reason === option.value
              const Glyph = option.icon
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setReason(option.value)}
                  className={cn(
                    `flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left transition`,
                    option.wide && `sm:col-span-2`,
                    selected
                      ? `border-red-500 bg-red-50/70 shadow-elevation-1`
                      : `border-default hover:border-strong hover:bg-surface-subtle`,
                  )}
                >
                  <Glyph
                    size={16}
                    weight="bold"
                    className={cn(`shrink-0`, selected ? `text-red-600` : `text-minimal`)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className={cn(`block truncate text-body-sm`, selected ? `text-red-900` : `text-default`)}>
                      {option.value}
                    </span>
                    {/* The plain-English line under each: "Insurance: State /
                          HMO" is a category name, not a sentence, and staff
                          should not have to work out which bucket they are in. */}
                    <span className="block truncate text-body-xs text-minimal">{option.hint}</span>
                  </span>
                  {selected && <CheckCircleIcon size={16} weight="fill" className="shrink-0 text-red-600" />}
                </button>
              )
            })}
          </div>
        </div>

        {!isSMC && (
          <label
            className={cn(
              `flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2.5 transition`,
              disqualify ? `border-strong bg-surface-subtle` : `border-default hover:bg-surface-subtle`,
            )}
          >
            <Checkbox
              className="mt-0.5"
              checked={disqualify}
              onCheckedChange={(value) => setDisqualify(value === true)}
            />
            <span className="min-w-0">
              <span className="block text-body-sm text-default">Disqualify this lead</span>
              <span className="block text-body-xs text-subtle">
                They were never a real opportunity, so they stop counting in lead totals and conversion rates.
              </span>
            </span>
          </label>
        )}

        <div>
          <div className="flex items-baseline justify-between">
            <p className="text-label-sm text-default">Note</p>
            <p className="text-body-xs text-minimal">Optional</p>
          </div>
          {/* The same editor the drawer's full form uses. The note is stored
                as markdown and the activity card renders it as markdown, so a
                plain textarea was quietly the odd one out.

                autoFocus off: the first thing to answer is the reason, and an
                editor that grabs the caret on open puts the cursor at the end
                of the form. */}
          <div className="mt-2 overflow-hidden rounded-lg border border-default">
            <RichTextEditor
              namespace="Action-Center-Closed-Lost-Note"
              placeholder="Anything worth knowing next time they come up"
              autoFocus={false}
              onChange={setNote}
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-default bg-surface-subtle/60 px-6 py-3.5">
        <p className="min-w-0 truncate text-body-xs text-subtle">Closed by {user.name}</p>
        <div className="flex shrink-0 gap-2">
          <Button variant="white" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="danger" disabled={!reason} onClick={submit}>
            Close lead
          </Button>
        </div>
      </div>
    </div>
  )
}
